import { useEffect, useState } from 'react'
import type { FieldProps } from '@rjsf/utils'
import { Mtds } from 'prove-common'
import { useGetReinbeitedistrikterQuery } from '../../../hooks/useReinbeitedistrikter'
import type { ReinbeitedistriktValue } from '../../../types/types'
import { requiredFieldMessage } from '../../../utils/requiredFieldMessage'

const SEARCH_DEBOUNCE_MS = 500
const MAX_SEARCH_LENGTH = 256

const collectErrorMessages = (errorSchema: unknown): string[] => {
  if (errorSchema == null || typeof errorSchema !== 'object') return []
  const schema = errorSchema as Record<string, unknown>
  const ownErrors = Array.isArray(schema.__errors)
    ? schema.__errors.filter(
        (error): error is string => typeof error === 'string'
      )
    : []
  return [
    ...ownErrors,
    ...Object.entries(schema).flatMap(([key, value]) =>
      key === '__errors' ? [] : collectErrorMessages(value)
    )
  ]
}

const getErrorMessages = (props: Readonly<FieldProps>, title: string) => {
  if (props.rawErrors?.length) return props.rawErrors
  const nestedErrors = collectErrorMessages(props.errorSchema)
  return nestedErrors.length > 0 ? [requiredFieldMessage(title)] : []
}

const getSelectedValue = (
  value: unknown
): ReinbeitedistriktValue | undefined => {
  if (value == null || typeof value !== 'object') return undefined
  const candidate = value as Partial<ReinbeitedistriktValue>
  if (
    typeof candidate.codevalue !== 'string' ||
    typeof candidate.label !== 'string'
  ) {
    return undefined
  }
  return { codevalue: candidate.codevalue, label: candidate.label }
}

export function ReinbeitedistriktField(props: Readonly<FieldProps>) {
  const {
    id,
    formData,
    onChange,
    disabled = false,
    readonly = false,
    required = false,
    schema,
    fieldPathId
  } = props
  const selected = getSelectedValue(formData)
  const [searchText, setSearchText] = useState(selected?.label ?? '')
  const [debouncedSearch, setDebouncedSearch] = useState(searchText)
  const title =
    typeof schema?.title === 'string' ? schema.title : 'Reinbeitedistrikt'
  const description =
    typeof schema?.description === 'string' ? schema.description : undefined
  const fieldId = id || fieldPathId?.$id || 'reinbeitedistrikt'
  const path = fieldPathId?.path
  const isInteractive = !disabled && !readonly
  const searchIsSettled = searchText === debouncedSearch
  const { data, isFetching, isError, refetch } = useGetReinbeitedistrikterQuery(
    debouncedSearch,
    isInteractive
  )
  const errorMessages = Array.from(new Set(getErrorMessages(props, title)))

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(searchText),
      SEARCH_DEBOUNCE_MS
    )
    return () => clearTimeout(timer)
  }, [searchText])

  useEffect(() => {
    if (!selected) return
    setSearchText(selected.label)
    setDebouncedSearch(selected.label)
  }, [selected?.codevalue, selected?.label])

  const options = (searchIsSettled ? (data ?? []) : []).map((district) => ({
    value: district.codevalue,
    label: district.label,
    children: `${district.label} (${district.codevalue})`
  }))

  const handleSelectedChange = (selection: Array<{ value: string }>) => {
    const district = (data ?? []).find(
      (result) => result.codevalue === selection[0]?.value
    )
    if (!district) return
    setSearchText(district.label)
    setDebouncedSearch(district.label)
    onChange(
      {
        codevalue: district.codevalue,
        label: district.label
      },
      path
    )
  }

  const handleClear = (event: React.FormEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setSearchText('')
    setDebouncedSearch('')
    onChange(undefined, path)
  }

  const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value.slice(0, MAX_SEARCH_LENGTH)
    setSearchText(value)
    if (selected && value !== selected.label) onChange(undefined, path)
  }

  if (!isInteractive) {
    return (
      <Mtds.Field
        as='input'
        id={fieldId}
        label={title}
        helpText={description}
        helpTextLabel={`Mer informasjon om ${title}`}
        value={
          selected
            ? `${selected.label} (${selected.codevalue})`
            : 'Ingen reinbeitedistrikt valgt'
        }
        disabled
        readOnly
      />
    )
  }

  return (
    <Mtds.Grid data-gap='2'>
      <Mtds.Fieldset>
        <Mtds.Fieldset.Legend>{title}</Mtds.Fieldset.Legend>
        {description && (
          <Mtds.HelpText
            data-size='xs'
            data-tooltip={description}
            aria-label={`Mer informasjon om ${title}`}
          >
            {description}
          </Mtds.HelpText>
        )}
        <Mtds.Field>
          <Mtds.Field.Label htmlFor={fieldId} hidden>
            {title}
          </Mtds.Field.Label>
          <Mtds.Field.Suggestion
            data-nofilter
            selected={
              selected
                ? [{ value: selected.codevalue, label: selected.label }]
                : []
            }
            onSelectedChange={handleSelectedChange}
            options={options}
          >
            <Mtds.Input
              id={fieldId}
              aria-label={title}
              type='search'
              placeholder='Søk etter navn, distriktskode eller nummer.'
              value={searchText}
              maxLength={MAX_SEARCH_LENGTH}
              aria-required={required}
              onInput={handleInput}
            />
            {selected && (
              <button
                type='reset'
                aria-label='Fjern valgt reinbeitedistrikt'
                onClick={handleClear}
              />
            )}
          </Mtds.Field.Suggestion>
        </Mtds.Field>
      </Mtds.Fieldset>

      {selected && <Mtds.Muted>Distriktskode: {selected.codevalue}</Mtds.Muted>}

      {isFetching && (
        <Mtds.Flex data-align='center' data-gap='2' role='status'>
          <Mtds.Spinner />
          <Mtds.Muted>Henter reinbeitedistrikter...</Mtds.Muted>
        </Mtds.Flex>
      )}
      {isError && (
        <Mtds.Alert data-color='warning' role='alert'>
          <p>Kunne ikke hente reinbeitedistrikter.</p>
          <Mtds.Button
            type='button'
            data-variant='secondary'
            onClick={() => refetch()}
          >
            Prøv igjen
          </Mtds.Button>
        </Mtds.Alert>
      )}
      {searchIsSettled && !isFetching && !isError && data?.length === 0 && (
        <Mtds.Muted>Ingen reinbeitedistrikter funnet.</Mtds.Muted>
      )}
      {errorMessages.map((message) => (
        <Mtds.Validation key={`${fieldId}-${message}`}>
          {message}
        </Mtds.Validation>
      ))}
    </Mtds.Grid>
  )
}
